import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../core/auth/AuthContext";
import { useAuthUI } from "../../core/auth/AuthUIContext";
import CertifiedCelebration from "../../components/ui/CertifiedCelebration";
import { getCertificationKind } from "./certificationKinds";
import { getMemberCertificationRequest, respondToMemberCertification } from "./memberCertification.api";
import styles from "../organizerInvitation/OrganizerInvitationPage.module.css";

// Page ouverte depuis la notification « Demande de certification » (ADR-020) :
// la personne accepte ou refuse d'etre certifiee par le membre qui la propose.
const MEMBER = getCertificationKind("MEMBER");

export default function MemberCertificationPage({ requestId }) {
  const { user, loading: isAuthLoading } = useAuth();
  const { openAuthModal } = useAuthUI();
  const userId = user?.id || user?.uid || null;
  const [state, setState] = useState({ status: "loading", request: null });
  const [pendingDecision, setPendingDecision] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [isCelebrating, setIsCelebrating] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setState({ status: "anonymous", request: null });
      return;
    }
    try {
      const request = await getMemberCertificationRequest(requestId);
      setState({ status: request ? "ready" : "not-found", request });
    } catch (error) {
      setState({ status: error?.status === 404 ? "not-found" : "error", request: null });
    }
  }, [requestId, userId]);

  useEffect(() => {
    if (!isAuthLoading) load();
  }, [isAuthLoading, load]);

  const respond = async (accept) => {
    setPendingDecision(accept ? "accept" : "decline");
    setSubmitError("");
    try {
      const result = await respondToMemberCertification(requestId, accept);
      if (accept && result?.status === "ACCEPTED") setIsCelebrating(true);
      await load();
    } catch (error) {
      setSubmitError(error?.message || "Ta réponse n'a pas pu être enregistrée. Réessaie dans un instant.");
    } finally {
      setPendingDecision(null);
    }
  };

  const request = state.request;
  const certifierName = request?.certifier?.displayName || "Un membre";
  let message = null;
  let showActions = false;

  if (state.status === "loading" || isAuthLoading) {
    message = <p className={styles.muted}>Chargement de la demande…</p>;
  } else if (state.status === "anonymous") {
    message = (
      <button type="button" className={styles.primaryButton} onClick={() => openAuthModal?.()}>
        Se connecter pour répondre
      </button>
    );
  } else if (state.status === "not-found") {
    message = <p className={styles.muted}>Cette demande est introuvable.</p>;
  } else if (state.status === "error") {
    message = (
      <>
        <p className={styles.muted}>Impossible de charger la demande pour le moment.</p>
        <button type="button" className={styles.secondaryButton} onClick={load}>Réessayer</button>
      </>
    );
  } else if (request.targetCertified) {
    message = <p className={styles.success}>Tu es utilisateur certifié ! Ton badge est actif.</p>;
  } else if (!request.isTarget) {
    message = <p className={styles.muted}>Cette demande est adressée à quelqu'un d'autre.</p>;
  } else if (request.status === "DECLINED") {
    message = <p className={styles.muted}>Tu as refusé cette certification.</p>;
  } else if (request.status !== "PENDING") {
    message = <p className={styles.muted}>Cette demande n'est plus valable.</p>;
  } else {
    showActions = true;
  }

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <img className={styles.logo} src={MEMBER.logo} alt="" width="112" height="112" />
        <p className={styles.kicker}>Certification KARANGO</p>
        <h1 className={styles.title}>
          {request ? `${certifierName} veut te certifier` : MEMBER.invitation.title}
        </h1>
        <p className={styles.lead}>
          En acceptant, tu deviens <strong>Utilisateur certifié</strong> : {certifierName} se porte garant·e de toi
          auprès de la communauté, et son nom apparaît sur ton badge.
        </p>
        <h2 className={styles.perksTitle}>Ce que tu pourras faire</h2>
        <ul className={styles.perks}>
          {MEMBER.invitation.perks.map((perk) => (
            <li key={perk.id} className={styles.perk}>
              <span className={styles.perkIcon} aria-hidden="true">{perk.icon}</span>
              <span>
                <strong className={styles.perkTitle}>{perk.title}</strong>
                <span className={styles.perkText}>{perk.description}</span>
              </span>
            </li>
          ))}
        </ul>
        <a className={styles.homeLink} href="/">Ouvrir Karango</a>
        {/* Zone de reponse fixee en bas de l'ecran : toujours visible pendant le defilement. */}
        <div className={styles.responseBar}>
          {message}
          {showActions && (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} disabled={Boolean(pendingDecision)} onClick={() => respond(true)}>
                {pendingDecision === "accept" ? "Un instant…" : "Accepter"}
              </button>
              <button type="button" className={styles.secondaryButton} disabled={Boolean(pendingDecision)} onClick={() => respond(false)}>
                {pendingDecision === "decline" ? "Un instant…" : "Refuser"}
              </button>
            </div>
          )}
          {submitError && <p className={styles.error} role="alert">{submitError}</p>}
        </div>
      </section>
      {isCelebrating && (
        <CertifiedCelebration
          kind="MEMBER"
          displayName={user?.username || user?.displayName}
          homeHref="/"
          onClose={() => setIsCelebrating(false)}
        />
      )}
    </main>
  );
}
